import { useGetTemplatesQuery, useUpdateWithTemplateMutation } from '@/core/services/report'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  DialogActions,
  CircularProgress,
  Button,
  Select,
  Chip,
} from '@mui/material'
import { useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { skipToken } from '@reduxjs/toolkit/query'

interface CreateReportModalProps {
  reportId: string
  transcriptionId: string
  userId: string
}

export function UpdateReportWithTemplate({ reportId, transcriptionId, userId }: CreateReportModalProps) {
  const { data: templates } = useGetTemplatesQuery(userId ? { userId } : skipToken, { refetchOnMountOrArgChange: true })
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [updateReport, { isLoading }] = useUpdateWithTemplateMutation()

  const handleSubmit = async () => {
    if (selectedTemplate && transcriptionId) {
      try {
        await updateReport({
          template: selectedTemplate,
          lastUpdatedBy: userId,
          reportId,
        }).unwrap()
        setReportModalOpen(false)
      } catch (error) {
        console.error('Failed to create report:', error)
      }
    }
  }

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setReportModalOpen(true)}>
        <Trans>Change template</Trans>
      </Button>

      <Dialog open={reportModalOpen} onClose={() => setReportModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Trans>Update with another template</Trans>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }}>
            <Trans>Choose a report template to get started</Trans>
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="template-select-label">
              <Trans>Report template</Trans>
            </InputLabel>
            <Select labelId="template-select-label" value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)} label={t`Report template`}>
              {templates?.map((template) => (
                <MenuItem key={template._id} value={template._id}>
                  {template.isDefault ? (
                    <>
                      {template.title}
                      <Chip sx={{ ml: 1 }} label={t`default`} color="secondary" size="small" />
                    </>
                  ) : (
                    template.title
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportModalOpen(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!selectedTemplate || !transcriptionId || isLoading}>
            {isLoading ? (
              <>
                <CircularProgress size="20px" color="inherit" />
                <Trans>Generation in progress...</Trans>
              </>
            ) : (
              <Trans>Update</Trans>
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
